// Documents & Vault — the metadata catalog over the file vault. The bytes live
// on the storage share behind the API; these rows describe them and link each
// file to an optional parent (organization / site / personnel / detail /
// incident). Two panes: a filterable left list of documents and a right detail
// pane for the selected document (metadata + download + edit + delete). A
// collapsible drawer surfaces the append-only folder-lifecycle journal.
//
// Uploads send the raw file body (not a JSON POST); metadata travels as query
// params via the useUploadDocument hook. Edits are metadata-only — replacing
// the bytes is a fresh upload.
//
// NOTE: route + nav link for this page are wired in App.tsx / NavMenu during
// the Integration pass.

import { useMemo, useState, type FormEvent } from 'react';
import type {
  DocumentType,
  DocumentWithRefs,
  DocumentFolderEventType,
} from '@dsr/shared-types';
import { can } from '../auth/store';
import { DateInput } from '../components/DateInput';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Table,
} from '../components/ui';
import { ApiError } from '../api/client';
import {
  useDocuments,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument,
  useDocumentFolderAudit,
  useOrganizationsLookup,
  useSitesLookup,
  downloadDocument,
  type DocumentFilters,
  type FolderAuditFilters,
} from '../api/queries/documents';

// ---------- Static option lists ----------

const DOCUMENT_TYPES: { value: DocumentType; label: string }[] = [
  { value: 'contract', label: 'Contract' },
  { value: 'post_order', label: 'Post Order' },
  { value: 'sop', label: 'SOP' },
  { value: 'license', label: 'License' },
  { value: 'cert', label: 'Certification' },
  { value: 'authorization', label: 'Authorization' },
  { value: 'survey', label: 'Site Survey' },
  { value: 'incident_evidence', label: 'Incident Evidence' },
  { value: 'dar', label: 'Daily Activity Report' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'advance', label: 'Advance' },
  { value: 'misc', label: 'Miscellaneous' },
];

const FOLDER_EVENT_TYPES: DocumentFolderEventType[] = ['create', 'rename', 'delete'];

function docTypeLabel(t: DocumentType): string {
  return DOCUMENT_TYPES.find((d) => d.value === t)?.label ?? t;
}

function errMessage(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function fmtDate(epochMs: number | null): string {
  return epochMs !== null ? new Date(epochMs).toLocaleString() : '—';
}

function fmtDay(epochMs: number | null): string {
  return epochMs !== null ? new Date(epochMs).toLocaleDateString() : '—';
}

function fmtSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// A document is "expiring" if it has an expiry within the next 30 days (or
// already lapsed). Drives the warning badge in the list/detail.
const EXPIRY_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
function expiryTone(expiresAt: number | null): 'badge-muted' | 'badge-warn' | 'badge-danger' {
  if (expiresAt === null) return 'badge-muted';
  const now = Date.now();
  if (expiresAt <= now) return 'badge-danger';
  if (expiresAt - now <= EXPIRY_WINDOW_MS) return 'badge-warn';
  return 'badge-muted';
}

// =============================================================================
// Page
// =============================================================================

export function DocumentsPage(): JSX.Element {
  const canWrite = can('document.write');
  const canDelete = can('document.delete');

  const [filters, setFilters] = useState<DocumentFilters>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<DocumentWithRefs | null>(null);
  const [deleting, setDeleting] = useState<DocumentWithRefs | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const documentsQuery = useDocuments(filters);
  const deleteDocument = useDeleteDocument();

  const documents = documentsQuery.data?.documents ?? [];
  const selected = useMemo(
    () => documents.find((d) => d.id === selectedId) ?? null,
    [documents, selectedId],
  );

  const onConfirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    try {
      await deleteDocument.mutateAsync(deleting.id);
      toast.success(`Deleted “${deleting.title ?? `document #${deleting.id}`}”.`);
      if (selectedId === deleting.id) setSelectedId(null);
      setDeleting(null);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to delete document.'));
    }
  };

  const onDownload = async (doc: DocumentWithRefs): Promise<void> => {
    try {
      await downloadDocument(doc);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to download document.'));
    }
  };

  return (
    <div className="documents-page">
      <header className="page-header">
        <div>
          <h2>Documents &amp; Vault</h2>
          <p className="page-sub">
            Catalog of files stored in the secure vault — contracts, post orders,
            licenses, evidence, and generated reports.
          </p>
        </div>
        <div className="page-header-actions">
          <Button
            variant="ghost"
            onClick={() => setAuditOpen((o) => !o)}
            aria-expanded={auditOpen}
          >
            {auditOpen ? 'Hide folder log' : 'Folder log'}
          </Button>
          {canWrite && (
            <Button variant="primary" onClick={() => setUploadOpen(true)}>
              Upload document
            </Button>
          )}
        </div>
      </header>

      <DocumentFilterBar filters={filters} onChange={setFilters} />

      {auditOpen && <FolderAuditDrawer />}

      <div className="split-layout">
        <section className="split-list" aria-label="Documents">
          {documentsQuery.isLoading && <p className="muted">Loading documents…</p>}
          {documentsQuery.isError && (
            <p className="error-text" role="alert">
              {errMessage(documentsQuery.error, 'Failed to load documents.')}
            </p>
          )}
          {!documentsQuery.isLoading && documents.length === 0 && (
            <p className="muted empty-state">No documents match these filters.</p>
          )}

          <ul className="list-cards">
            {documents.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  className={`list-card ${selectedId === d.id ? 'active' : ''}`}
                  onClick={() => setSelectedId(d.id)}
                  aria-pressed={selectedId === d.id}
                >
                  <div className="list-card-main">
                    <span className="list-card-title">
                      {d.title ?? `Document #${d.id}`}
                    </span>
                    <Badge tone="neutral">{docTypeLabel(d.document_type)}</Badge>
                  </div>
                  <div className="list-card-meta">
                    {d.organization_name && (
                      <span className="list-card-desc">{d.organization_name}</span>
                    )}
                    {d.site_name && (
                      <span className="list-card-desc">{d.site_name}</span>
                    )}
                    {d.expires_at !== null && (
                      <span className={`badge ${expiryTone(d.expires_at)}`}>
                        Expires {fmtDay(d.expires_at)}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section className="split-detail" aria-label="Document detail">
          {!selected ? (
            <div className="detail-placeholder muted">
              Select a document to view its metadata and download it.
            </div>
          ) : (
            <DocumentDetail
              key={selected.id}
              doc={selected}
              canWrite={canWrite}
              canDelete={canDelete}
              onDownload={() => void onDownload(selected)}
              onEdit={() => setEditing(selected)}
              onDelete={() => setDeleting(selected)}
            />
          )}
        </section>
      </div>

      {/* Upload */}
      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={(d) => {
            setUploadOpen(false);
            setSelectedId(d.id);
          }}
        />
      )}

      {/* Edit metadata */}
      {editing && (
        <MetadataModal
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => setEditing(null)}
        />
      )}

      {/* Delete */}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete document"
        message={
          deleting
            ? `Delete “${deleting.title ?? `document #${deleting.id}`}”? The catalog entry is soft-deleted and the vault bytes are removed.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={deleteDocument.isPending}
        onConfirm={onConfirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

// =============================================================================
// Filter bar
// =============================================================================

interface DocumentFilterBarProps {
  filters: DocumentFilters;
  onChange: (next: DocumentFilters) => void;
}

function DocumentFilterBar({ filters, onChange }: DocumentFilterBarProps): JSX.Element {
  const orgsQuery = useOrganizationsLookup();
  const sitesQuery = useSitesLookup();
  const orgs = orgsQuery.data?.organizations ?? [];
  const sites = sitesQuery.data?.sites ?? [];

  return (
    <div className="page-toolbar">
      <FormField className="inline" label="Type">
        <select
          className="form-input"
          value={filters.document_type ?? ''}
          onChange={(e) =>
            onChange({
              ...filters,
              document_type: e.target.value
                ? (e.target.value as DocumentType)
                : undefined,
            })
          }
        >
          <option value="">All types</option>
          {DOCUMENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </FormField>

      <FormField className="inline" label="Organization">
        <select
          className="form-input"
          value={filters.organization_id ?? ''}
          onChange={(e) =>
            onChange({
              ...filters,
              organization_id: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        >
          <option value="">All organizations</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField className="inline" label="Site">
        <select
          className="form-input"
          value={filters.site_id ?? ''}
          onChange={(e) =>
            onChange({
              ...filters,
              site_id: e.target.value ? Number(e.target.value) : undefined,
            })
          }
        >
          <option value="">All sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField className="inline" label="Expiring before">
        <DateInput
          value={filters.expiring_before ?? null}
          onChange={(epochMs) =>
            onChange({ ...filters, expiring_before: epochMs ?? undefined })
          }
          placeholder="any"
        />
      </FormField>

      {(filters.document_type ||
        filters.organization_id !== undefined ||
        filters.site_id !== undefined ||
        filters.expiring_before !== undefined) && (
        <Button variant="ghost" className="btn-sm" onClick={() => onChange({})}>
          Clear filters
        </Button>
      )}
    </div>
  );
}

// =============================================================================
// Document detail
// =============================================================================

interface DocumentDetailProps {
  doc: DocumentWithRefs;
  canWrite: boolean;
  canDelete: boolean;
  onDownload: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function DocumentDetail({
  doc,
  canWrite,
  canDelete,
  onDownload,
  onEdit,
  onDelete,
}: DocumentDetailProps): JSX.Element {
  return (
    <div className="document-detail">
      <header className="detail-header">
        <div>
          <h3>{doc.title ?? `Document #${doc.id}`}</h3>
          <div className="detail-subline">
            <Badge tone="neutral">{docTypeLabel(doc.document_type)}</Badge>
            {doc.expires_at !== null && (
              <span className={`badge ${expiryTone(doc.expires_at)}`}>
                Expires {fmtDay(doc.expires_at)}
              </span>
            )}
          </div>
        </div>
        <div className="detail-actions">
          <Button variant="primary" onClick={onDownload}>
            Download
          </Button>
          {canWrite && (
            <Button variant="ghost" onClick={onEdit}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button variant="danger" onClick={onDelete}>
              Delete
            </Button>
          )}
        </div>
      </header>

      <dl className="detail-grid">
        <div>
          <dt>Type</dt>
          <dd>{docTypeLabel(doc.document_type)}</dd>
        </div>
        <div>
          <dt>MIME type</dt>
          <dd>{doc.mime_type ?? '—'}</dd>
        </div>
        <div>
          <dt>Size</dt>
          <dd>{fmtSize(doc.file_size)}</dd>
        </div>
        <div>
          <dt>Organization</dt>
          <dd>{doc.organization_name ?? '—'}</dd>
        </div>
        <div>
          <dt>Site</dt>
          <dd>{doc.site_name ?? '—'}</dd>
        </div>
        <div>
          <dt>Personnel ID</dt>
          <dd>{doc.personnel_id ?? '—'}</dd>
        </div>
        <div>
          <dt>Detail ID</dt>
          <dd>{doc.detail_id ?? '—'}</dd>
        </div>
        <div>
          <dt>Incident ID</dt>
          <dd>{doc.incident_id ?? '—'}</dd>
        </div>
        <div>
          <dt>Expires</dt>
          <dd>{fmtDay(doc.expires_at)}</dd>
        </div>
        <div>
          <dt>Uploaded by</dt>
          <dd>{doc.created_by_name ?? '—'}</dd>
        </div>
        <div>
          <dt>Uploaded</dt>
          <dd>{fmtDate(doc.created_at)}</dd>
        </div>
        <div>
          <dt>Last updated</dt>
          <dd>{fmtDate(doc.updated_at)}</dd>
        </div>
        <div className="detail-grid-wide">
          <dt>Vault path</dt>
          <dd className="mono">{doc.rel_path}</dd>
        </div>
      </dl>
    </div>
  );
}

// =============================================================================
// Parent reference fields (shared by upload + edit)
// =============================================================================

interface ParentRefs {
  organization_id: number | null;
  site_id: number | null;
  personnel_id: number | null;
  detail_id: number | null;
  incident_id: number | null;
}

interface ParentRefFieldsProps {
  value: ParentRefs;
  onChange: (next: ParentRefs) => void;
  disabled?: boolean;
}

function ParentRefFields({ value, onChange, disabled }: ParentRefFieldsProps): JSX.Element {
  const orgsQuery = useOrganizationsLookup();
  const sitesQuery = useSitesLookup();
  const orgs = orgsQuery.data?.organizations ?? [];
  const sites = sitesQuery.data?.sites ?? [];

  const numField = (
    key: 'personnel_id' | 'detail_id' | 'incident_id',
    label: string,
  ): JSX.Element => (
    <FormField label={label}>
      <Input
        type="number"
        min={1}
        value={value[key] ?? ''}
        disabled={disabled}
        onChange={(e) =>
          onChange({ ...value, [key]: e.target.value ? Number(e.target.value) : null })
        }
        placeholder="optional"
      />
    </FormField>
  );

  return (
    <>
      <FormField label="Organization">
        <select
          className="form-input"
          value={value.organization_id ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              ...value,
              organization_id: e.target.value ? Number(e.target.value) : null,
            })
          }
        >
          <option value="">None</option>
          {orgs.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </FormField>

      <FormField label="Site">
        <select
          className="form-input"
          value={value.site_id ?? ''}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ...value, site_id: e.target.value ? Number(e.target.value) : null })
          }
        >
          <option value="">None</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </FormField>

      {numField('personnel_id', 'Personnel ID')}
      {numField('detail_id', 'Detail ID')}
      {numField('incident_id', 'Incident ID')}
    </>
  );
}

const EMPTY_PARENTS: ParentRefs = {
  organization_id: null,
  site_id: null,
  personnel_id: null,
  detail_id: null,
  incident_id: null,
};

// =============================================================================
// Upload modal
// =============================================================================

interface UploadModalProps {
  onClose: () => void;
  onUploaded: (doc: DocumentWithRefs) => void;
}

function UploadModal({ onClose, onUploaded }: UploadModalProps): JSX.Element {
  const upload = useUploadDocument();
  const busy = upload.isPending;

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('misc');
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [parents, setParents] = useState<ParentRefs>(EMPTY_PARENTS);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) {
      toast.warn('Choose a file to upload.');
      return;
    }
    const trimmedTitle = title.trim();
    try {
      const res = await upload.mutateAsync({
        file,
        document_type: documentType,
        title: trimmedTitle ? trimmedTitle : undefined,
        expires_at: expiresAt,
        organization_id: parents.organization_id,
        site_id: parents.site_id,
        personnel_id: parents.personnel_id,
        detail_id: parents.detail_id,
        incident_id: parents.incident_id,
      });
      toast.success(`Uploaded “${res.document.title ?? file.name}”.`);
      onUploaded(res.document);
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to upload document.'));
    }
  };

  return (
    <Dialog
      title="Upload document"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="document-upload-form"
            variant="primary"
            disabled={busy}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </>
      }
    >
      <form id="document-upload-form" onSubmit={onSubmit} className="form-grid">
        <FormField label="File">
          <Input
            type="file"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              // Default the title to the filename if the user hasn't typed one.
              if (f && title.trim() === '') setTitle(f.name);
            }}
            required
          />
        </FormField>

        <FormField label="Title">
          <Input
            type="text"
            value={title}
            disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
            placeholder="Defaults to the filename"
          />
        </FormField>

        <FormField label="Type">
          <select
            className="form-input"
            value={documentType}
            disabled={busy}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
          >
            {DOCUMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Expires">
          <DateInput
            value={expiresAt}
            onChange={setExpiresAt}
            disabled={busy}
            placeholder="never"
          />
        </FormField>

        <ParentRefFields value={parents} onChange={setParents} disabled={busy} />
      </form>
    </Dialog>
  );
}

// =============================================================================
// Edit-metadata modal (no byte changes — re-upload to replace bytes)
// =============================================================================

interface MetadataModalProps {
  doc: DocumentWithRefs;
  onClose: () => void;
  onSaved: () => void;
}

function MetadataModal({ doc, onClose, onSaved }: MetadataModalProps): JSX.Element {
  const update = useUpdateDocument();
  const busy = update.isPending;

  const [title, setTitle] = useState(doc.title ?? '');
  const [documentType, setDocumentType] = useState<DocumentType>(doc.document_type);
  const [expiresAt, setExpiresAt] = useState<number | null>(doc.expires_at);
  const [parents, setParents] = useState<ParentRefs>({
    organization_id: doc.organization_id,
    site_id: doc.site_id,
    personnel_id: doc.personnel_id,
    detail_id: doc.detail_id,
    incident_id: doc.incident_id,
  });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const trimmedTitle = title.trim();
    try {
      await update.mutateAsync({
        id: doc.id,
        document_type: documentType,
        title: trimmedTitle ? trimmedTitle : null,
        expires_at: expiresAt,
        organization_id: parents.organization_id,
        site_id: parents.site_id,
        personnel_id: parents.personnel_id,
        detail_id: parents.detail_id,
        incident_id: parents.incident_id,
      });
      toast.success('Document metadata updated.');
      onSaved();
    } catch (err) {
      toast.danger(errMessage(err, 'Failed to update document.'));
    }
  };

  return (
    <Dialog
      title="Edit document"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="document-metadata-form"
            variant="primary"
            disabled={busy}
          >
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </>
      }
    >
      <form id="document-metadata-form" onSubmit={onSubmit} className="form-grid">
        <p className="muted form-hint">
          Metadata only — to replace the file itself, upload a new document.
        </p>

        <FormField label="Title">
          <Input
            type="text"
            value={title}
            disabled={busy}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={500}
            autoFocus
          />
        </FormField>

        <FormField label="Type">
          <select
            className="form-input"
            value={documentType}
            disabled={busy}
            onChange={(e) => setDocumentType(e.target.value as DocumentType)}
          >
            {DOCUMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField label="Expires">
          <DateInput
            value={expiresAt}
            onChange={setExpiresAt}
            disabled={busy}
            placeholder="never"
          />
        </FormField>

        <ParentRefFields value={parents} onChange={setParents} disabled={busy} />
      </form>
    </Dialog>
  );
}

// =============================================================================
// Folder lifecycle journal (append-only) — collapsible drawer
// =============================================================================

function FolderAuditDrawer(): JSX.Element {
  const [filters, setFilters] = useState<FolderAuditFilters>({});
  const auditQuery = useDocumentFolderAudit(filters);
  const events = auditQuery.data?.events ?? [];

  return (
    <section className="dashboard-card folder-audit" aria-label="Folder lifecycle log">
      <div className="detail-section-head">
        <h4>Folder lifecycle log</h4>
        <FormField className="inline" label="Event">
          <select
            className="form-input"
            value={filters.event_type ?? ''}
            onChange={(e) =>
              setFilters({
                event_type: e.target.value
                  ? (e.target.value as DocumentFolderEventType)
                  : undefined,
              })
            }
          >
            <option value="">All events</option>
            {FOLDER_EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </FormField>
      </div>

      {auditQuery.isLoading && <p className="muted">Loading folder log…</p>}
      {auditQuery.isError && (
        <p className="error-text" role="alert">
          {errMessage(auditQuery.error, 'Failed to load folder log.')}
        </p>
      )}
      {!auditQuery.isLoading && events.length === 0 && (
        <p className="muted empty-state">No folder events recorded.</p>
      )}

      {events.length > 0 && (
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.HeaderCell>When</Table.HeaderCell>
              <Table.HeaderCell>Event</Table.HeaderCell>
              <Table.HeaderCell>Path</Table.HeaderCell>
              <Table.HeaderCell>Detail</Table.HeaderCell>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {events.map((ev) => (
              <Table.Row key={ev.id}>
                <Table.Cell>{fmtDate(ev.event_at)}</Table.Cell>
                <Table.Cell>
                  <span className="badge badge-muted">{ev.event_type}</span>
                </Table.Cell>
                <Table.Cell className="mono cell-truncate">{ev.rel_path ?? '—'}</Table.Cell>
                <Table.Cell className="cell-truncate">{ev.detail ?? '—'}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </section>
  );
}
